import DealFilters from '@/components/all-from-router/_components/deal-filters/deal-filters';
import {
  FilteredDealsProvider,
  useFilteredDealsContext,
} from '@/components/all-from-router/_components/deal-filters/filtered-deals/filtered-deals-provider';
import {
  getPaymentCopy,
  getTradeInCopy,
  getVehicleCopy,
} from '@/components/delivery-board-from-router/_components/deal-card-copy';
import VirtualizedDealBoard from '@/components/delivery-board-from-router/_components/virtualized-deal-board';
import FindInViewFilter from '@/components/ui/find-in-view-filter/find-in-view-filter';
import { useFindInViewFilterContext } from '@/components/ui/find-in-view-filter/find-in-view-filter-provider';
import { HubListHeader } from '@/components/ui/layout/headers';
import HotkeyDialogBoxes from '@/components/ux/all-list-and-board/hotkey-dialog-boxes';
import useAllListAndBoardHotkeys from '@/components/ux/all-list-and-board/use-all-list-and-board-hotkeys';
import useDealsByDelivery from '@/hooks/use-deals-for-delivery';
import { type DealListItem } from '@/models/types';
import { createFileRoute } from '@tanstack/react-router';
import { observer } from 'mobx-react-lite';
import type { ReactNode } from 'react';
import { useCallback } from 'react';

export const Route = createFileRoute('/(authenticated)/$hub/delivery-queue/')({
  component: AllPageWrapper,
  staticData: {
    keepAlive: true,
  },
});

const DeliveryQueuePage = observer(() => {
  const { dealsByDeliveryData: dealsData } = useDealsByDelivery();
  const { filterCallback, shouldApplyFilteredDealsIds } =
    useFilteredDealsContext();

  const { openDialog, setOpenDialog, selectedDeal, enableScope } =
    useAllListAndBoardHotkeys();

  const { findInViewQuery } = useFindInViewFilterContext();

  const findInViewFilterCallback = useCallback(
    (deal: DealListItem) => {
      const query = findInViewQuery.toLowerCase();
      const vehicleInfo = deal.vehicle
        ? getVehicleCopy({ vehicle: deal.vehicle })
        : '';
      const tradeInInfo = getTradeInCopy({ tradeIns: deal.trades });
      const paymentInfo = getPaymentCopy({ payments: deal.payments });
      const vin = deal.vehicle?.vehicle?.vin;

      return (
        deal.vehicle?.vehicle?.vin.toLowerCase().includes(query) ||
        vehicleInfo.toLowerCase().includes(query) ||
        tradeInInfo.toLowerCase().includes(query) ||
        deal.status.toLowerCase().includes(query) ||
        deal.rapid_recon_step?.toLowerCase().includes(query) ||
        paymentInfo.toLowerCase().includes(query) ||
        vin?.toLowerCase().includes(query) ||
        deal.customer_name?.toLowerCase().includes(query) ||
        deal.customer_email?.toLowerCase().includes(query) ||
        !!deal.co_registrant_name?.toLowerCase().includes(query)
      );
    },
    [findInViewQuery],
  );

  return (
    <div className="flex flex-col h-screen">
      <HubListHeader
        findInViewFilter={<FindInViewFilter />}
        subRow={<DealFilters />}
      />

      <VirtualizedDealBoard
        dealsData={dealsData}
        filterCallback={filterCallback}
        enableScope={enableScope}
        shouldApplyFilteredDealsIds={shouldApplyFilteredDealsIds}
        findInViewFilterCallback={findInViewFilterCallback}
      />
      <HotkeyDialogBoxes
        openDialog={openDialog}
        setOpenDialog={setOpenDialog}
        selectedDeal={selectedDeal}
      />
    </div>
  );
});

export const DealBoardFilteredDealsProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  return (
    <FilteredDealsProvider
      initialFilteredState={[
        {
          filterMetaState: {
            type: 'delivery_queued',
            position: 0,
            isNegated: false,
            isCurrentlyBeingBuilt: false,
          },
          selectedFilters: {
            queued: true,
          },
        },
        {
          filterMetaState: {
            type: 'status',
            position: 0,
            isNegated: true,
            isCurrentlyBeingBuilt: false,
          },
          selectedFilters: {
            draft: false,
            new: false,
            in_progress: false,
            awaiting_delivery: false,
            delivered: true,
            done: true,
            cancelled: true,
            returning: false,
            returned: false,
          },
        },
      ]}
      initialMatchPreference="match-all-filters"
    >
      {children}
    </FilteredDealsProvider>
  );
};

function AllPageWrapper() {
  return (
    <DealBoardFilteredDealsProvider>
      <DeliveryQueuePage />
    </DealBoardFilteredDealsProvider>
  );
}
